import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Stack,
  Container
} from '@mui/material';
import {
  Add as AddIcon,
  Description as TemplateIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import SearchBar from './SearchBar';

const RunbookList = ({ runbooks, onViewRunbook, onCreateRunbook, onCreateFromTemplate, onSearchResults, familyId, onDeleteRunbook }) => {

  
  const categories = [...new Set(runbooks.map(r => r.category))];

  // Helper function to safely get step count
  const getStepCount = (runbook) => {
    try {
      if (!runbook.steps) return 0;
      if (Array.isArray(runbook.steps)) return runbook.steps.length;
      if (typeof runbook.steps === 'object') return 1; // Single step object
      return 0;
    } catch (error) {
      console.error('Error getting step count for runbook:', runbook.id, error);
      return 0;
    }
  };

  // Helper function to safely render text
  const safeText = (value) => {
    try {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number') return String(value);
      if (typeof value === 'boolean') return String(value);
      if (typeof value === 'object') {
        // If it's a step object, try to get the title or description
        if (value.title) return value.title;
        if (value.description) return value.description;
        // Otherwise, convert to string representation
        try {
          return JSON.stringify(value);
        } catch {
          return '[Object]';
        }
      }
      return String(value);
    } catch (error) {
      console.error('Error in safeText:', error, 'Value:', value);
      return '[Error]';
    }
  };

  const handleSearchResults = (results, query) => {
    onSearchResults(results, query);
  };

  const handleClearSearch = () => {
    onSearchResults(null, '');
  };

  const handleDeleteClick = (e, runbook) => {
    e.stopPropagation(); // Prevent card click event
    onDeleteRunbook(runbook.id);
  };

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 0.5, sm: 2 } }}>
      <Box sx={{ py: { xs: 1.5, sm: 3 } }}>
        {/* Header Section */}
        <Box sx={{ mb: { xs: 2, sm: 4 } }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: { xs: 2, sm: 3 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 0 }
          }}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              Available Runbooks
            </Typography>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2}
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                '& > *': {
                  minWidth: { xs: '100%', sm: 'auto' }
                }
              }}
            >
              <Button
                variant="outlined"
                startIcon={<TemplateIcon />}
                onClick={onCreateFromTemplate}
                sx={{ 
                  minWidth: { xs: '100%', sm: 140 },
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                }}
              >
                Use Template
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreateRunbook}
                sx={{ 
                  minWidth: { xs: '100%', sm: 160 },
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                }}
              >
                Create New Runbook
              </Button>
            </Stack>
          </Box>

          <SearchBar 
            onSearchResults={handleSearchResults}
            onClearSearch={handleClearSearch}
            familyId={familyId}
          />
        </Box>

        {/* Categories and Runbooks */}
        {categories.map(category => (
          <Box key={category} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CategoryIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" component="h2" gutterBottom>
                {category}
              </Typography>
            </Box>
            
            <Grid container spacing={{ xs: 1.5, sm: 3 }}>
              {runbooks
                .filter(runbook => runbook.category === category)
                .map(runbook => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={runbook.id}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                      }}
                      onClick={() => onViewRunbook(runbook)}
                    >
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start', 
                          mb: 1,
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 1, sm: 0 }
                        }}>
                          <Typography 
                            variant="h6" 
                            component="h3" 
                            sx={{ 
                              fontWeight: 600,
                              lineHeight: 1.3,
                              flex: 1,
                              mr: { xs: 0, sm: 1 },
                              fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}
                          >
                            {safeText(runbook.title)}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteClick(e, runbook)}
                            sx={{ 
                              color: 'error.main',
                              '&:hover': { backgroundColor: 'error.light' },
                              alignSelf: { xs: 'flex-end', sm: 'auto' }
                            }}
                            title="Delete runbook"
                            aria-label={`Delete ${safeText(runbook.title)}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: { xs: 2, sm: 3 },
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.4,
                            fontSize: { xs: '0.875rem', sm: '0.875rem' }
                          }}
                        >
                          {safeText(runbook.description)}
                        </Typography>
                      </CardContent>
                      
                      <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          width: '100%',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 1, sm: 0 }
                        }}>
                          <Chip
                            label={`${getStepCount(runbook)} steps`}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                          />
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            color: 'primary.main',
                            alignSelf: { xs: 'flex-end', sm: 'auto' }
                          }}>
                            <Typography variant="body2" sx={{ mr: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              View
                            </Typography>
                            <ViewIcon fontSize="small" />
                          </Box>
                        </Box>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
            </Grid>
          </Box>
        ))}

        {/* Empty State */}
        {runbooks.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" component="h3" gutterBottom color="text.secondary">
              No runbooks available
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Create your first runbook to get started!
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateRunbook}
              size="large"
            >
              Create Your First Runbook
            </Button>
          </Box>
        )}
      </Box>


    </Container>
  );
};

export default RunbookList;